import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, apiForm } from "../api/client";
import { useAuth } from "../context/AuthContext";

function UploadIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
    </svg>
  );
}

function ChevronDown({ open }) {
  return (
    <svg className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916" />
    </svg>
  );
}

export default function Templates() {
  const { token, user, activeCompany } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const [templates, setTemplates] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listErr, setListErr] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState(null);

  const [expandedId, setExpandedId] = useState(null);
  const [templateDetail, setTemplateDetail] = useState({});
  const [detailLoading, setDetailLoading] = useState({});

  const [deletingId, setDeletingId] = useState(null);
  const [deleteErr, setDeleteErr] = useState(null);

  const [duplicateErr, setDuplicateErr] = useState(null);

  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");

  // Success toast
  const [toast, setToast] = useState(
    location.state?.created ? `Template "${location.state.name}" saved.` : null
  );
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  async function loadTemplates() {
    setLoadingList(true);
    setListErr(null);
    try {
      const data = await api("/india/templates", { token });
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      setListErr(e?.message || "Failed to load templates");
    } finally {
      setLoadingList(false);
    }
  }

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function toggleExpand(id) {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (templateDetail[id]) return;
    setDetailLoading(p => ({ ...p, [id]: true }));
    try {
      const data = await api(`/india/templates/${id}`, { token });
      setTemplateDetail(p => ({ ...p, [id]: data }));
    } catch {
      setTemplateDetail(p => ({ ...p, [id]: null }));
    } finally {
      setDetailLoading(p => ({ ...p, [id]: false }));
    }
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadErr(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const result = await apiForm("/india/templates/import", fd, { token });
      // Nothing is saved yet — the parsed content loads into the builder as an
      // unsaved draft for review, same mechanism as Duplicate.
      navigate("/templates/new", { state: { draftPayload: result.template, importReport: result.report } });
    } catch (e) {
      const msg = e?.message || "";
      let detail = msg;
      try {
        const jsonStart = msg.indexOf("{");
        if (jsonStart !== -1) {
          const parsed = JSON.parse(msg.slice(jsonStart));
          if (parsed?.detail) detail = typeof parsed.detail === "string" ? parsed.detail : JSON.stringify(parsed.detail);
        }
      } catch { /* not JSON, use raw message */ }
      setUploadErr(detail || "Import failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }


  async function handleDelete(id, name) {
    if (!window.confirm(`Delete template "${name}"?\n\nThis cannot be undone.`)) return;
    setDeleteErr(null);
    setDeletingId(id);
    try {
      await api(`/india/templates/${id}`, { token, method: "DELETE" });
      setTemplates(prev => prev.filter(t => t.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch (e) {
      setDeleteErr(e?.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleDuplicate(tpl) {
    setDuplicateErr(null);
    try {
      const detail = templateDetail[tpl.id] || await api(`/india/templates/${tpl.id}`, { token });
      navigate("/templates/new", { state: { draftPayload: detail } });
    } catch (e) {
      setDuplicateErr(e?.message || "Failed to load template");
    }
  }

  return (
    <div style={{ paddingTop: 32, paddingLeft: 24, paddingRight: 24, maxWidth: 896, margin: "0 auto" }}>

      {toast && (
        <div style={{
          marginBottom: 16, fontSize: 14, color: "#2e7d32",
          border: "1px solid #c3e6cb", background: "#e6f4ea", borderRadius: 10, padding: "12px 16px",
        }}>
          ✓ {toast}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: "#1e3a5f", margin: 0 }}>Templates</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            disabled={creating}
            onClick={async () => {
              setCreateErr("");
              setCreating(true);
              try {
                const res = await api("/india/inspections/draft", { token, method: "POST", body: { location_type: "vessel" } });
                navigate(`/inspections/${res.inspection_id}/setup`);
              } catch (e) {
                setCreateErr(e?.message || "Failed to create inspection");
              } finally {
                setCreating(false);
              }
            }}
            style={{
              padding: "8px 16px", borderRadius: 8, background: "#1e3a5f", color: "white",
              fontSize: 14, border: "none", cursor: creating ? "not-allowed" : "pointer", opacity: creating ? 0.5 : 1,
            }}
          >
            {creating ? "Creating…" : "Create inspection"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/templates/new")}
            style={{
              padding: "8px 16px", borderRadius: 8, background: "#1e3a5f", color: "white",
              fontSize: 14, border: "none", cursor: "pointer",
            }}
          >
            + New template
          </button>
        </div>
      </div>
      {createErr && (
        <div style={{
          fontSize: 14, color: "#c0392b", border: "1px solid #fdecea",
          borderRadius: 8, padding: 12, marginBottom: 16, background: "#fdecea",
        }}>{createErr}</div>
      )}

      {/* Import from Word or Excel — Team & Business */}
      <section style={{ marginBottom: 32, border: "1px solid #d0dcea", borderRadius: 12, background: "white", padding: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1e3a5f", marginBottom: 4 }}>
              Import from Word or Excel <span style={{ fontSize: 11, fontWeight: 500, color: "#6b8cae" }}>(Team &amp; Business)</span>
            </div>
            <div style={{ fontSize: 12, color: "#6b8cae", maxWidth: 448 }}>
              Upload an existing checklist — a filled-in copy of the example{" "}
              <code style={{ background: "#f1f5f9", padding: "1px 4px", borderRadius: 3 }}>Template</code> sheet (.xlsx),
              or a Word document (.docx) with numbered section headings and question tables. Nothing is saved
              until you review it — you'll land in the template builder with everything pre-filled to check first.
            </div>
          </div>
          
            <a href="/oskar_template_import_format.xlsx"
            download
            style={{
              flexShrink: 0, fontSize: 12, padding: "6px 12px", border: "1px solid #d0dcea",
              borderRadius: 6, background: "white", color: "#6b8cae", textDecoration: "none",
            }}
          >
            Download example file
          </a>
        </div>

        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <input ref={fileInputRef} type="file" accept=".xlsx,.docx" style={{ display: "none" }} onChange={handleFileChange} />
          <button
            type="button"
            disabled={uploading}
            onClick={() => { setUploadErr(null); fileInputRef.current?.click(); }}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "8px 16px", borderRadius: 8, border: "1px solid #d0dcea",
              background: "white", fontSize: 14, fontWeight: 500,
              cursor: uploading ? "not-allowed" : "pointer", opacity: uploading ? 0.5 : 1,
            }}
          >
            <UploadIcon />
            {uploading ? "Importing…" : "Choose .xlsx or .docx file"}
          </button>
        </div>

        {uploadErr && (
          <div style={{ marginTop: 16, fontSize: 14, color: "#c0392b", border: "1px solid #fdecea", borderRadius: 10, background: "#fdecea", padding: 12 }}>
            {uploadErr}
            {uploadErr.includes("Team and Business plans") && (
              <> · <a href="/plans" style={{ color: "#c0392b", fontWeight: 600 }}>View plans →</a></>
            )}
          </div>
        )}
      </section>
      {/* Template list */}
      <section>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1e3a5f" }}>Existing templates</div>
          {(deleteErr || duplicateErr) && <div style={{ fontSize: 12, color: "#c0392b" }}>{deleteErr || duplicateErr}</div>}
        </div>

        {loadingList && <div style={{ fontSize: 14, color: "#6b8cae" }}>Loading…</div>}
        {!loadingList && listErr && (
          <div style={{ fontSize: 14, color: "#c0392b", border: "1px solid #fdecea", borderRadius: 10, padding: 12, background: "#fdecea" }}>{listErr}</div>
        )}
        {!loadingList && !listErr && templates.length === 0 && (
          <div style={{ fontSize: 14, color: "#6b8cae", border: "1px dashed #d0dcea", borderRadius: 12, padding: 32, textAlign: "center" }}>
            No templates yet. Create one with the builder or import from Excel.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {templates.map((tpl) => {
            const isOpen = expandedId === tpl.id;
            const detail = templateDetail[tpl.id];
            const loading = detailLoading[tpl.id];
            const totalQ = detail
              ? detail.sections?.reduce((acc, s) => acc + (s.questions?.length || 0), 0)
              : null;
            const missingRules = detail
              ? (detail.sections || []).flatMap(s => s.questions || []).filter(q => !q.compliance_rule).length
              : null;

            return (
              <div key={tpl.id} style={{ border: "1px solid #d0dcea", borderRadius: 12, background: "white", overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => toggleExpand(tpl.id)}
                    style={{ display: "flex", alignItems: "center", gap: 8, textAlign: "left", flex: 1, minWidth: 0, background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  >
                    <ChevronDown open={isOpen} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: "#1e3a5f", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 8 }}>
                        {tpl.name}
                        {tpl.is_system && (
                          <span style={{ fontSize: 12, padding: "2px 6px", borderRadius: 4, background: "#e8eef5", color: "#1e3a5f", fontWeight: 500, flexShrink: 0 }}>System</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b8cae", marginTop: 2, display: "flex", alignItems: "center", gap: 8 }}>
                        {tpl.code && <span style={{ fontFamily: "monospace" }}>{tpl.code}</span>}
                        {totalQ != null && <span>· {totalQ} questions</span>}
                        {missingRules != null && missingRules > 0 && (
                          <span style={{ color: "#92610a", fontWeight: 500 }}>⚠ {missingRules} missing rules</span>
                        )}
                        {tpl.active === false && <span style={{ color: "#92610a" }}>inactive</span>}
                      </div>
                    </div>
                  </button>

                  {/* Edit template — owner or admin only, never for system templates (matches backend guard) */}
                  {!tpl.is_system && (Number(tpl.created_by_user_id) === Number(user?.id) || user?.role === "admin") && (
                    <button
                      type="button"
                      onClick={() => navigate(`/templates/${tpl.id}/edit`)}
                      style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 8, border: "1px solid #d0dcea", fontSize: 12, fontWeight: 500, background: "white", cursor: "pointer" }}
                      title="Edit this template"
                    >
                      Edit
                    </button>
                  )}

                  {/* Edit rules button — always visible, never for system templates */}
                  {!tpl.is_system && (
                    <button
                      type="button"
                      onClick={() => navigate(`/templates/${tpl.id}/rules`)}
                      style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 8, border: "1px solid #d0dcea", fontSize: 12, fontWeight: 500, background: "white", cursor: "pointer" }}
                      title="Set or edit compliance rules for this template"
                    >
                      Edit rules
                    </button>
                  )}

                  {/* Duplicate template — always visible, including system templates */}
                  <button
                    type="button"
                    onClick={() => handleDuplicate(tpl)}
                    style={{
                      flexShrink: 0, padding: "6px 12px", borderRadius: 8, border: "1px solid #d0dcea", fontSize: 12, fontWeight: 500, background: "white",
                      cursor: "pointer",
                    }}
                    title="Duplicate this template"
                  >
                    Duplicate
                  </button>
                  
                  {/* Delete template — owner or admin only, never for system templates (matches backend guard) */}
                  {!tpl.is_system && (Number(tpl.created_by_user_id) === Number(user?.id) || user?.role === "admin") && (
                    <button
                      type="button"
                      onClick={() => handleDelete(tpl.id, tpl.name)}
                      disabled={deletingId === tpl.id}
                      style={{
                        flexShrink: 0, padding: 6, borderRadius: 6,
                        background: "none", border: "none", color: "#6b8cae",
                        cursor: deletingId === tpl.id ? "not-allowed" : "pointer",
                        opacity: deletingId === tpl.id ? 0.4 : 1,
                      }}
                      title="Delete template"
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>

                {isOpen && (
                  <div style={{ borderTop: "1px solid #edf2f7", padding: "12px 16px", background: "#f8fafc" }}>
                    {loading && <div style={{ fontSize: 12, color: "#6b8cae" }}>Loading…</div>}
                    {!loading && detail === null && <div style={{ fontSize: 12, color: "#c0392b" }}>Failed to load detail.</div>}
                    {!loading && detail && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        {tpl.description && <p style={{ fontSize: 12, color: "#6b8cae", margin: 0 }}>{tpl.description}</p>}
                        {detail.sections?.map((sec) => (
                          <div key={sec.id}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#3a5068", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.08em" }}>{sec.title}</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              {sec.questions?.map((q) => (
                                <div key={q.id} style={{ fontSize: 12, color: "#3a5068", background: "white", border: "1px solid #edf2f7", borderRadius: 6, padding: "6px 12px" }}>
                                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                                    <span style={{ flexShrink: 0, fontFamily: "monospace", color: "#6b8cae", width: 64, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.code || "—"}</span>
                                    <span style={{ flex: 1 }}>{q.text}</span>
                                    <span style={{ flexShrink: 0, color: "#6b8cae", background: "#f1f5f9", borderRadius: 4, padding: "2px 6px", fontFamily: "monospace" }}>{q.type}</span>
                                    {q.compliance_rule ? (
                                      <span style={{ flexShrink: 0, color: "#1e3a5f", background: "#e8eef5", border: "1px solid #d0dcea", borderRadius: 4, padding: "2px 6px" }}>
                                        {q.compliance_rule}{q.expected_value ? ` = ${q.expected_value}` : ""}
                                      </span>
                                    ) : (
                                      <span style={{ flexShrink: 0, color: "#92610a", background: "#fef9ec", border: "1px dashed #f5d97e", borderRadius: 4, padding: "2px 6px", fontStyle: "italic" }}>
                                        no rule
                                      </span>
                                    )}
                                    {q.severity && q.severity !== "major" && (
                                      <span style={{
                                        flexShrink: 0, borderRadius: 4, padding: "2px 6px",
                                        background: q.severity === "critical" ? "#fdecea" : "#fef9ec",
                                        color: q.severity === "critical" ? "#c0392b" : "#92610a",
                                      }}>
                                        {q.severity}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

    </div>
  );
}
